import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { FaArrowLeft, FaFileExcel, FaFilePdf, FaMoneyBillWave } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import depositosApi from "../../services/api/depositosApi";
import "react-toastify/dist/ReactToastify.css";

const formatDate = (date) => date.toISOString().split("T")[0];

const REPORT_TYPES = {
  detallado: {
    label: "Listado Detallado",
    format: "Excel",
    description: "Detalle completo de depósitos exportado en Excel.",
  },
  agrupado_dia: {
    label: "Agrupado por Día, Centro de Costo y Tipo de Depósito",
    format: "PDF",
    description:
      "Por fecha de solicitud: detalle diario por centro de costo y tipo, con subtotal por día y total general.",
  },
  resumen_dia_centro: {
    label: "Agrupado por Día y Centro de Costo",
    format: "PDF",
    description:
      "Por fecha de solicitud: una fila por centro de costo en cada día, total del día y total general al final.",
  },
  agrupado: {
    label: "Agrupado por Centro de Costo y Tipo de Depósito",
    format: "PDF",
    description: "Totales del periodo agrupados por centro de costo y tipo de depósito.",
  },
  gerencia: {
    label: "Reporte Gerencia",
    format: "PDF",
    description:
      "Primera hoja: total por centro de costo del periodo. Siguientes hojas: agrupado por día y centro de costo.",
  },
};

const ReporteDepositosCorporativo = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");

  const [loadingCentros, setLoadingCentros] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [formData, setFormData] = useState({
    tipoReporte: "detallado",
    centro_costo_id: "todos",
    fecha_desde: formatDate(new Date()),
    fecha_hasta: formatDate(new Date()),
  });

  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Reporte de Depósitos | SGC";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const loadCentros = async () => {
      try {
        setLoadingCentros(true);
        const response = await depositosApi.getCentrosCosto(true);
        setCentrosCosto(response?.data || []);
      } catch (error) {
        console.error("Error al cargar centros de costo:", error);
        toast.error("No se pudo cargar la lista de centros de costo");
      } finally {
        setLoadingCentros(false);
      }
    };

    loadCentros();
  }, []);

  const reportConfig = useMemo(
    () => REPORT_TYPES[formData.tipoReporte] || REPORT_TYPES.detallado,
    [formData.tipoReporte]
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildFilters = () => {
    const filters = {
      fecha_desde: formData.fecha_desde,
      fecha_hasta: formData.fecha_hasta,
    };

    if (formData.centro_costo_id !== "todos") {
      filters.centro_costo_id = parseInt(formData.centro_costo_id, 10);
    }

    return filters;
  };

  const validateForm = () => {
    if (!formData.fecha_desde || !formData.fecha_hasta) {
      toast.error("Debe seleccionar el rango de fechas");
      return false;
    }

    if (new Date(formData.fecha_desde) > new Date(formData.fecha_hasta)) {
      toast.error("La fecha desde no puede ser mayor a la fecha hasta");
      return false;
    }

    return true;
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleGenerate = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      setGenerating(true);
      const filters = buildFilters();

      if (formData.tipoReporte === "detallado") {
        const response = await depositosApi.exportExcelDetallado(filters);
        downloadBlob(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          `depositos_detallado_${formatDate(new Date())}.xlsx`
        );
        toast.success("Reporte detallado generado correctamente");
      } else if (formData.tipoReporte === "agrupado_dia") {
        const response = await depositosApi.exportPdfAgrupadoDiaCentroTipo(filters);
        downloadBlob(new Blob([response.data], { type: "application/pdf" }), `depositos_agrupado_dia_${formatDate(new Date())}.pdf`);
        toast.success("Agrupado por día, centro de costo y tipo generado correctamente");
      } else if (formData.tipoReporte === "resumen_dia_centro") {
        const response = await depositosApi.exportPdfResumenDiaCentroCosto(filters);
        downloadBlob(
          new Blob([response.data], { type: "application/pdf" }),
          `depositos_agrupado_dia_centro_${formatDate(new Date())}.pdf`
        );
        toast.success("Agrupado por día y centro de costo generado correctamente");
      } else if (formData.tipoReporte === "gerencia") {
        const response = await depositosApi.exportPdfGerencia(filters);
        downloadBlob(
          new Blob([response.data], { type: "application/pdf" }),
          `depositos_gerencia_${formatDate(new Date())}.pdf`
        );
        toast.success("Reporte Gerencia generado correctamente");
      } else {
        const response = await depositosApi.exportReporteAgrupadoCentroCostoPdf(filters);
        downloadBlob(
          new Blob([response.data], { type: "application/pdf" }),
          `depositos_agrupado_${formatDate(new Date())}.pdf`
        );
        toast.success("Reporte agrupado generado correctamente");
      }
    } catch (error) {
      console.error("Error al generar reporte de depósitos:", error);
      toast.error(error.response?.data?.detail || "No se pudo generar el reporte");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      {/* Digital Harmony Styles para Reportes (mismo lenguaje que ReporteComensalesComedor, ReportesIndex, etc.) */}
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title {
            font-size: 2rem;
            font-weight: 700;
            margin: 0;
          }
          .dh-main-subtitle {
            font-size: 1.05rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.75rem;
            border: none;
            font-weight: 700;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          .btn-dh-primary:hover:not(:disabled) {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          .dh-form-label {
            color: #374151;
            font-weight: 600;
            font-size: 0.95rem;
            margin-bottom: 8px;
          }
          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          .dh-alert-info {
            background: linear-gradient(135deg, rgba(0, 198, 255, 0.1) 0%, rgba(0, 114, 255, 0.1) 100%);
            border-left: 4px solid #00c6ff;
            color: #0056d1;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <ToastContainer />

        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center">
                <FaMoneyBillWave className="me-3" size={36} />
                <div>
                  <h1 className="dh-main-title">Reporte de Depósitos</h1>
                  <p className="dh-main-subtitle mb-0">Corporativo · Digital Harmony</p>
                </div>
              </div>
              <Button className="btn-dh-secondary d-flex align-items-center" onClick={() => navigate("/reportes")}>
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="pb-5">
          <Row className="justify-content-center">
            <Col xl={8} lg={10}>
              <Card className="dh-card border-0">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaMoneyBillWave className="me-2" />
                    Parámetros y exportación
                  </h5>
                </Card.Header>

                <Card.Body className="p-4">
                  <div className="dh-alert dh-alert-info mb-4">
                    Seleccione el tipo de reporte, el rango de fechas y el centro de costo. Puede usar
                    <strong> Todos</strong> para incluir todos los centros de costo. Los reportes por día usan la{" "}
                    <strong>fecha de solicitud</strong> de cada depósito. Los PDF por día incluyen total por día y total
                    general al final.
                  </div>

                  <Form onSubmit={handleGenerate}>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Tipo de reporte</Form.Label>
                          <Form.Select
                            name="tipoReporte"
                            value={formData.tipoReporte}
                            onChange={handleInputChange}
                            className="dh-form-control"
                          >
                            {Object.entries(REPORT_TYPES).map(([value, config]) => (
                              <option key={value} value={value}>
                                {config.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            {reportConfig.description} · Formato: {reportConfig.format}
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Fecha desde</Form.Label>
                          <Form.Control
                            type="date"
                            name="fecha_desde"
                            value={formData.fecha_desde}
                            onChange={handleInputChange}
                            required
                            className="dh-form-control"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Fecha hasta</Form.Label>
                          <Form.Control
                            type="date"
                            name="fecha_hasta"
                            value={formData.fecha_hasta}
                            onChange={handleInputChange}
                            required
                            className="dh-form-control"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Centro de costo</Form.Label>
                          <Form.Select
                            name="centro_costo_id"
                            value={formData.centro_costo_id}
                            onChange={handleInputChange}
                            disabled={loadingCentros}
                            className="dh-form-control"
                          >
                            <option value="todos">Todos</option>
                            {centrosCosto.map((centro) => (
                              <option key={centro.id} value={centro.id}>
                                {centro.codigo ? `${centro.codigo} - ` : ""}
                                {centro.nombre}
                              </option>
                            ))}
                          </Form.Select>
                          {loadingCentros && (
                            <Form.Text className="text-muted">Cargando centros de costo...</Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        type="submit"
                        className="btn-dh-primary d-flex align-items-center"
                        disabled={generating || loadingCentros}
                      >
                        {generating ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Generando...
                          </>
                        ) : (
                          <>
                            {formData.tipoReporte === "detallado" ? (
                              <FaFileExcel className="me-2" />
                            ) : (
                              <FaFilePdf className="me-2" />
                            )}
                            Generar {reportConfig.format}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ReporteDepositosCorporativo;
